//src/pages/Empresas.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import { toast } from "react-toastify";
import {
  Building2,
  ChevronDown,
  ChevronUp,
  Edit3,
  FileSpreadsheet,
  Plus,
  Search,
  Users,
} from "lucide-react";

import ModalSemPermissao from "../components/ModalSemPermissao";
import "./Empresas.css";

function Empresas() {
  const [empresas, setEmpresas] = useState([]);
  const [termo, setTermo] = useState("");
  const [pagina, setPagina] = useState(1);
  const [total, setTotal] = useState(0);

  const [clientes, setClientes] = useState([]);
  const [empresaAbertaId, setEmpresaAbertaId] = useState(null);

  const navigate = useNavigate();
  const usuario = JSON.parse(localStorage.getItem("usuario"));

  const podeVerEmpresas =
    usuario?.tipo === "admin" ||
    usuario?.permissoes?.some((p) => p.rota === "empresas" && p.pode_ler);

  const podeInserirEmpresas =
    usuario?.tipo === "admin" ||
    usuario?.permissoes?.some((p) => p.rota === "empresas" && p.pode_inserir);

  const podeEditarEmpresas =
    usuario?.tipo === "admin" ||
    usuario?.permissoes?.some((p) => p.rota === "empresas" && p.pode_editar);

  const podeImportarEmpresas =
    usuario?.tipo === "admin" ||
    usuario?.permissoes?.some((p) => p.rota === "empresas" && p.pode_inserir);

  const carregarEmpresas = () => {
    axios
      .get("/empresas", {
        params: {
          filtro: termo,
          pagina,
          limite: 20,
        },
      })
      .then((res) => {
        setEmpresas(res.data.dados || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => toast.error("Erro ao carregar empresas."));
  };

  useEffect(() => {
    carregarEmpresas();
  }, [termo, pagina]);

  useEffect(() => {
    const carregarClientes = async () => {
      try {
        const resp = await axios.get("/clientes");
        setClientes(resp.data || []);
      } catch (err) {
        console.error("Erro ao carregar clientes:", err);
      }
    };

    carregarClientes();
  }, []);

  const alternarStatus = async (id) => {
    try {
      await axios.put(`/empresas/${id}/alternar`);
      toast.success("Status atualizado com sucesso.");
      carregarEmpresas();
    } catch (err) {
      console.error("Erro ao alternar status:", err);
      toast.error("Erro ao atualizar status.");
    }
  };

  const alternarPix = async (id) => {
    try {
      await axios.put(`/empresas/${id}/alternar-pix`);
      toast.success("Status do Pix atualizado com sucesso.");
      carregarEmpresas();
    } catch (err) {
      console.error("Erro ao alternar Pix:", err);
      toast.error("Erro ao atualizar Pix.");
    }
  };

  const totalPaginas = Math.max(1, Math.ceil(total / 20));

  const toggleAccordion = (id) => {
    setEmpresaAbertaId((prev) => (prev === id ? null : id));
  };

  const resumo = useMemo(() => {
    return {
      total,
      ativas: empresas.filter((e) => Number(e.ativa) === 1 || e.ativa === true).length,
      pix: empresas.filter((e) => Number(e.pix_ativa) === 1 || e.pix_ativa === true).length,
      clientes: clientes.length,
    };
  }, [empresas, total, clientes]);

  const clientesPorEmpresa = useMemo(() => {
    return clientes.reduce((acc, cliente) => {
      const cnpj = cliente.cnpj_empresa;
      if (!cnpj) return acc;

      if (!acc[cnpj]) acc[cnpj] = [];
      acc[cnpj].push(cliente);

      return acc;
    }, {});
  }, [clientes]);

  if (!podeVerEmpresas) {
    return <ModalSemPermissao onClose={() => (window.location.href = "/")} />;
  }

  return (
    <div className="empresasAdmin-page">
      <div className="empresasAdmin-topbar">
        <div>
          <span className="empresasAdmin-kicker">Área administrativa</span>
          <h2>Empresas</h2>
          <p>
            Gerencie empresas participantes, status do Tikpontos, Pix Premiado e
            usuários vinculados a cada CNPJ.
          </p>
        </div>

        <div className="empresasAdmin-actions">
          {podeImportarEmpresas && (
            <button
              type="button"
              className="empresasAdmin-btn secondary"
              onClick={() => navigate("/empresas/importar")}
            >
              <FileSpreadsheet size={16} />
              Importar CSV
            </button>
          )}

          {podeInserirEmpresas && (
            <button
              type="button"
              className="empresasAdmin-btn primary"
              onClick={() => navigate("/empresas/nova")}
            >
              <Plus size={16} />
              Nova empresa
            </button>
          )}
        </div>
      </div>

      <div className="empresasAdmin-cards">
        <div className="empresasAdmin-card">
          <span>Total cadastrado</span>
          <strong>{Number(resumo.total || 0).toLocaleString("pt-BR")}</strong>
        </div>

        <div className="empresasAdmin-card ativo">
          <span>Ativas nesta página</span>
          <strong>{resumo.ativas.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="empresasAdmin-card pix">
          <span>Pix ativo nesta página</span>
          <strong>{resumo.pix.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="empresasAdmin-card clientes">
          <span>Clientes carregados</span>
          <strong>{resumo.clientes.toLocaleString("pt-BR")}</strong>
        </div>
      </div>

      <div className="empresasAdmin-filtros">
        <div className="empresasAdmin-search">
          <Search size={15} />
          <input
            placeholder="Buscar por razão social ou CNPJ..."
            value={termo}
            onChange={(e) => {
              setTermo(e.target.value);
              setPagina(1);
            }}
          />
        </div>
      </div>

      <div className="empresasAdmin-tableCard">
        <div className="empresasAdmin-tableHeader">
          <div>
            <h3>Lista de empresas</h3>
            <span>{Number(total || 0).toLocaleString("pt-BR")} resultado(s)</span>
          </div>

          <Building2 size={18} />
        </div>

        <div className="empresasAdmin-tableWrapper">
          <table className="empresasAdmin-table">
            <thead>
              <tr>
                <th>CNPJ</th>
                <th>Razão social</th>
                <th>Tikpontos</th>
                <th>Pix Premiado</th>
                <th>Usuários</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {empresas.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empresasAdmin-empty">
                    Nenhuma empresa encontrada.
                  </td>
                </tr>
              ) : (
                empresas.map((empresa) => {
                  const clientesDaEmpresa = clientesPorEmpresa[empresa.cnpj] || [];
                  const aberta = empresaAbertaId === empresa.id;

                  return (
                    <React.Fragment key={empresa.id}>
                      <tr>
                        <td>
                          <strong>{empresa.cnpj}</strong>
                        </td>

                        <td>
                          <div className="empresasAdmin-empresaCell">
                            <div className="empresasAdmin-avatar">
                              {empresa.razao_social?.charAt(0)?.toUpperCase() || "E"}
                            </div>

                            <div>
                              <strong>{empresa.razao_social || "—"}</strong>
                              <span>ID #{empresa.id}</span>
                            </div>
                          </div>
                        </td>

                        <td>
                          <label className="empresasAdmin-switch">
                            <input
                              type="checkbox"
                              checked={!!empresa.ativa}
                              onChange={() => alternarStatus(empresa.id)}
                              disabled={!podeEditarEmpresas}
                            />
                            <span />
                          </label>
                        </td>

                        <td>
                          <label className="empresasAdmin-switch">
                            <input
                              type="checkbox"
                              checked={!!empresa.pix_ativa}
                              onChange={() => alternarPix(empresa.id)}
                              disabled={!podeEditarEmpresas}
                            />
                            <span />
                          </label>
                        </td>

                        <td>
                          <span className="empresasAdmin-badge usuarios">
                            <Users size={12} />
                            {clientesDaEmpresa.length} usuário(s)
                          </span>
                        </td>

                        <td>
                          <div className="empresasAdmin-rowActions">
                            {podeEditarEmpresas && (
                              <button
                                type="button"
                                className="empresasAdmin-actionBtn edit"
                                onClick={() => navigate(`/empresas/editar/${empresa.id}`)}
                              >
                                <Edit3 size={14} />
                                Editar
                              </button>
                            )}

                            <button
                              type="button"
                              className="empresasAdmin-actionBtn users"
                              onClick={() => toggleAccordion(empresa.id)}
                            >
                              {aberta ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              {aberta ? "Ocultar" : "Ver usuários"}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {aberta && (
                        <tr className="empresasAdmin-accordionRow">
                          <td colSpan={6}>
                            <div className="empresasAdmin-accordion">
                              <h4>Usuários vinculados a esta empresa</h4>

                              {clientesDaEmpresa.length === 0 ? (
                                <p>Nenhum usuário Tikpontos vinculado a este CNPJ.</p>
                              ) : (
                                <div className="empresasAdmin-clientesGrid">
                                  {clientesDaEmpresa.map((cli) => (
                                    <div className="empresasAdmin-clienteMini" key={cli.id}>
                                      <strong>{cli.nome}</strong>
                                      <span>{cli.email}</span>
                                      <small>{cli.cpf_cnpj || "Documento pendente"}</small>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="empresasAdmin-paginacao">
        <button
          type="button"
          disabled={pagina === 1}
          onClick={() => setPagina((p) => Math.max(1, p - 1))}
        >
          Anterior
        </button>

        <span>
          Página {pagina} de {totalPaginas}
        </span>

        <button
          type="button"
          disabled={pagina === totalPaginas}
          onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
        >
          Próxima
        </button>
      </div>
    </div>
  );
}

export default Empresas;